import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { useScenario } from "@/contexts/ScenarioContext";
import { useI18n } from "@/contexts/I18nContext";
import UnitMap from "@/components/UnitMap";
import GeoMap from "@/components/GeoMap";
import CriticalAlertsTable from "@/components/CriticalAlertsTable";
import TrafficChart from "@/components/TrafficChart";
import { Activity, ShieldAlert, Radio, ShieldCheck, ArrowUpRight, ArrowDownRight } from "lucide-react";

function KPICard({ label, value, unit, delta, icon: Icon, accent, testid, sub }) {
  const up = (delta ?? 0) >= 0;
  return (
    <div
      data-testid={testid}
      className="border border-slate-800 bg-[#0F172A] p-5 flex flex-col gap-3 hover:border-slate-700 transition-colors relative overflow-hidden"
    >
      <div className="absolute right-0 top-0 w-24 h-24 opacity-[0.04]" style={{ background: accent, filter: "blur(40px)" }} />
      <div className="flex items-center justify-between relative z-10">
        <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.2em] font-mono">
          {label}
        </div>
        <div className="w-8 h-8 flex items-center justify-center border" style={{ borderColor: accent + "40", background: accent + "10" }}>
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
      </div>
      <div className="flex items-baseline gap-2 relative z-10">
        <div className="font-mono font-bold tracking-tight text-white text-4xl">{value}</div>
        {unit && <div className="text-base text-slate-500 font-mono">{unit}</div>}
      </div>
      <div className="flex items-center justify-between relative z-10">
        {sub && <div className="text-[11px] text-slate-500 font-mono">{sub}</div>}
        {delta !== undefined && delta !== null && (
          <div className={`ml-auto flex items-center gap-1 text-[11px] font-mono font-bold ${up ? "text-emerald-400" : "text-red-400"}`}>
            {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(delta)}{Number.isFinite(delta) ? (label.toLowerCase().includes("alert") ? "" : "%") : ""}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { tenant } = useTenant();
  const { scenario } = useScenario();
  const { t, locale } = useI18n();
  const [kpis, setKpis] = useState(null);

  useEffect(() => {
    if (!tenant || !scenario) return;
    const load = () => api.get(`/dashboard/kpis?tenant=${tenant.id}&scenario=${scenario.id}`).then(({ data }) => setKpis(data));
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [tenant, scenario]);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between border-b border-slate-800 pb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-mono mb-2">
            {t("dash.subtitle")}
          </div>
          <h1 className="font-display text-4xl font-black tracking-tighter">{t("dash.title")}</h1>
          <p className="text-sm text-slate-400 mt-1">
            {t("dash.posture")} <span className="text-slate-100">{tenant?.name}</span>
            {scenario && scenario.id !== "live" && (
              <span className="ml-2 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border border-[#1E3A8A] text-[#60A5FA] bg-[#1E3A8A]/15 rounded-sm">
                {locale === "pt" ? scenario.label_pt : scenario.label}
              </span>
            )}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-mono">{t("dash.refresh")}</div>
          <div className="text-xs font-mono text-slate-300 flex items-center gap-2 mt-1 justify-end">
            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full evos-blink" />
            {t("dash.refresh_every")}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <KPICard testid="kpi-availability" label={t("kpi.availability")} value={kpis ? kpis.availability.value.toFixed(1) : "—"} unit="%" delta={kpis?.availability.delta} icon={Activity} accent="#10B981" />
        <KPICard testid="kpi-critical-alerts" label={t("kpi.critical_alerts")} value={kpis ? kpis.critical_alerts.value : "—"} delta={kpis?.critical_alerts.delta} icon={ShieldAlert} accent="#EF4444" sub={t("kpi.last_24h")} />
        <KPICard testid="kpi-connected-units" label={t("kpi.connected_units")} value={kpis ? kpis.connected_units.value : "—"} icon={Radio} accent="#3B82F6" sub={kpis ? `${kpis.connected_units.connected} ${t("kpi.online_of")} ${kpis.connected_units.total}` : ""} />
        <KPICard testid="kpi-compliance" label={t("kpi.compliance")} value={kpis ? kpis.compliance.value : "—"} unit="%" delta={kpis?.compliance.delta} icon={ShieldCheck} accent="#60A5FA" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-7 border border-slate-800 bg-[#0F172A]">
          <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
            <div>
              <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">{t("map.title")}</h3>
              <div className="text-[10px] text-slate-500 mt-0.5">{t("map.hint")}</div>
            </div>
            <div className="flex items-center gap-3 text-[10px] font-mono">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400" /><span className="text-slate-400">{t("map.healthy")}</span></span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400" /><span className="text-slate-400">{t("map.warning")}</span></span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" /><span className="text-slate-400">{t("map.down")}</span></span>
            </div>
          </div>
          <GeoMap />
        </div>

        <div className="xl:col-span-5 border border-slate-800 bg-[#0F172A]">
          <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
            <div>
              <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">{t("traffic.title")}</h3>
              <div className="text-[10px] text-slate-500 mt-0.5">{t("traffic.subtitle")}</div>
            </div>
            <span className="text-[10px] font-mono text-emerald-400 evos-blink">● {t("common.live")}</span>
          </div>
          <TrafficChart />
        </div>
      </div>

      <div className="border border-slate-800 bg-[#0F172A]">
        <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
          <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">{t("alerts.recent_critical")}</h3>
          <span className="text-[10px] font-mono text-slate-500">{t("alerts.actions_trigger")}</span>
        </div>
        <CriticalAlertsTable />
      </div>
    </div>
  );
}
